import { createFileRoute } from "@tanstack/react-router";
import { useForm } from "@tanstack/react-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@astryxdesign/core/Button";
import { TextInput } from "@astryxdesign/core/TextInput";
import * as stylex from "@stylexjs/stylex";
import { accessAPI, errorMessage, queries } from "../lib/api";
import { ruleSchema } from "../lib/schemas";
import { styles } from "../lib/styles";

export const Route = createFileRoute("/rules")({ component: RulesPage });
function RulesPage() {
  const state = useQuery(queries.state());
  const inventory = useQuery(queries.inventory());
  const cache = useQueryClient();
  const save = useMutation({
    mutationFn: accessAPI.saveRule,
    onSuccess: () => cache.invalidateQueries({ queryKey: ["access"] }),
  });
  const form = useForm({
    defaultValues: { id: "", server_id: "", group_id: "" },
    validators: { onSubmit: ruleSchema.pick({ id: true, server_id: true, group_id: true }) },
    onSubmit: async ({ value }) => {
      const group = inventory.data?.groups.find((g) => g.id === value.group_id);
      await save.mutateAsync(
        ruleSchema.parse({ ...value, group_name: group?.name ?? "", enabled: true }),
      );
    },
  });
  return (
    <>
      <div {...stylex.props(styles.pageHeading)}>
        <div>
          <h1 {...stylex.props(styles.title)}>Access rules</h1>
          <p {...stylex.props(styles.description)}>
            Map a designated SSH group to a server account. Membership comes from NetBird; shared
            setup-key devices do not imply a human identity.
          </p>
        </div>
      </div>
      {(state.error || inventory.error || save.error) && (
        <p role="alert" {...stylex.props(styles.error)}>
          {errorMessage(state.error || inventory.error || save.error)}
        </p>
      )}
      <section {...stylex.props(styles.panel)}>
        <h2 {...stylex.props(styles.panelHeading)}>Current mappings</h2>
        {state.data?.rules.length === 0 && (
          <p {...stylex.props(styles.empty)}>
            No mappings. Create a group in NetBird whose name starts with SSH-, then add it here.
          </p>
        )}
        {state.data?.rules.map((rule) => (
          <div key={rule.id} {...stylex.props(styles.pageHeading)}>
            <div>
              <strong>{rule.group_name}</strong>
              <p {...stylex.props(styles.small)}>
                {state.data.servers.find((s) => s.id === rule.server_id)?.alias ?? rule.server_id} /{" "}
                {state.data.servers.find((s) => s.id === rule.server_id)?.login}
              </p>
            </div>
            <div {...stylex.props(styles.nav)}>
              <span {...stylex.props(styles.status)}>{rule.enabled ? "Enabled" : "Disabled"}</span>
              <Button
                label={rule.enabled ? "Disable rule" : "Enable rule"}
                onClick={() => save.mutate({ ...rule, enabled: !rule.enabled })}
                isLoading={save.isPending}
              />
            </div>
          </div>
        ))}
      </section>
      <section {...stylex.props(styles.panel)}>
        <h2 {...stylex.props(styles.panelHeading)}>Add a mapping</h2>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void form.handleSubmit();
          }}
        >
          <div {...stylex.props(styles.form)}>
            <form.Field name="id">
              {(field) => (
                <TextInput
                  label="Rule ID"
                  value={field.state.value}
                  onChange={(value) => field.handleChange(value)}
                  placeholder="mmrs-engineers"
                  isRequired
                />
              )}
            </form.Field>
            <form.Field name="server_id">
              {(field) => (
                <label {...stylex.props(styles.field)}>
                  Server account
                  <select
                    {...stylex.props(styles.select)}
                    value={field.state.value}
                    onChange={(event) => field.handleChange(event.target.value)}
                    required
                  >
                    <option value="">Choose a server</option>
                    {state.data?.servers.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.alias} / {s.login}
                      </option>
                    ))}
                  </select>
                </label>
              )}
            </form.Field>
            <form.Field name="group_id">
              {(field) => (
                <label {...stylex.props(styles.field)}>
                  NetBird access group
                  <select
                    {...stylex.props(styles.select)}
                    value={field.state.value}
                    onChange={(event) => field.handleChange(event.target.value)}
                    required
                  >
                    <option value="">Choose an SSH- group</option>
                    {inventory.data?.groups.map((g) => (
                      <option key={g.id} value={g.id}>
                        {g.name}
                      </option>
                    ))}
                  </select>
                </label>
              )}
            </form.Field>
          </div>
          <div {...stylex.props(styles.actions)}>
            <Button
              label="Save mapping"
              type="submit"
              variant="primary"
              isLoading={save.isPending}
            />
            {save.isSuccess && <output>Mapping saved; synchronization will apply it.</output>}
          </div>
        </form>
      </section>
    </>
  );
}
